'use client';


export default function PrivacyPolicy() {
  return (
    <div className="relative min-h-screen bg-white py-32">

      <style jsx global>{` #global-contact-form { display: none !important; } `}</style>
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="w-full origin-center transform-gpu absolute -left-[40%] top-0">
          <div className="h-[1px] bg-gray-300 rotate-[296.7deg] w-[200%]" />
        </div>
        <div className="w-full origin-center transform-gpu absolute -right-[40%] top-0">
          <div className="h-[1px] bg-gray-300 rotate-[296.8deg] w-[200%]" />
        </div>
      </div>

      <div className="custom-container mx-auto px-4 relative z-10 max-w-7xl ">

        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-bold text-[#272d57] tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mt-2">Last updated: November 13, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed border shadow-[0_4px_54px_0_rgba(0,0,0,0.05)] border-gray-100 p-12 rounded-lg">

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mb-3">Introduction</h2>
          <p>
            This Privacy Policy describes the policies and procedures of MMB Digital Labs
            (“Make My Brand Labs”, “We”, “Us”, or “Our”) on the collection, use, and
            disclosure of Your information when You use our website  <a
              href="https://makemybrandlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            > makemybrandlabs.com </a>
            and explains Your privacy rights and how the law protects You.
          </p>
          <p>
            By using our Service, You agree to the collection and use of information in
            accordance with this Privacy Policy.
          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Interpretation and Definitions
          </h2>

          <h3 className="text-xl font-semibold text-[#272d57] mt-6 mb-2">Interpretation</h3>
          <p>
            Words with an initial capital letter have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or plural.
          </p>

          <h3 className="text-xl font-semibold text-[#272d57] mt-8 mb-2">Definitions</h3>
          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed">
            <li>
              <strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.
            </li>

            <li>
              <strong>Company</strong> (referred to as either “the Company”, “We”, “Us”, or “Our” in this Agreement)
              refers to MMB Digital Labs, operating as Make My Brand Labs.
            </li>

            <li>
              <strong>Cookies</strong> are small files placed on Your device by a website, containing details of Your browsing activity.
            </li>

            <li>
              <strong>Country</strong> refers to: India.
            </li>

            <li>
              <strong>Device</strong> means any device that can access the Service such as a computer, smartphone, or tablet.
            </li>

            <li>
              <strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.
            </li>

            <li>
              <strong>Service</strong> refers to the Website{" "}
              <a
                href="https://makemybrandlabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-600 hover:underline"
              >
                makemybrandlabs.com
              </a>.
            </li>

            <li>
              <strong>Service Provider</strong> means any natural or legal person who processes data on behalf of the Company,
              including third-party vendors or contractors.
            </li>

            <li>
              <strong>Usage Data</strong> refers to data collected automatically through use of the Service (e.g., time spent on
              pages, IP address).
            </li>

            <li>
              <strong>You</strong> means the individual accessing or using the Service, or the company or legal entity on behalf
              of which such individual is using the Service.
            </li>
          </ul>


          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Collecting and Using Your Personal Data
          </h2>

          <h3 className="text-xl font-semibold text-[#272d57] mt-6 mb-2">Types of Data Collected</h3>

          <h4 className="text-lg font-semibold text-[#272d57] mt-4 mb-2">Personal Data</h4>
          <p>While using Our Service, We may ask You to provide certain personally identifiable information that can be used to contact or identify You. This may include, but is not limited to:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>Name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Company name</li>
            <li>Usage Data</li>
          </ul>

          <h4 className="text-lg font-semibold text-[#272d57] mt-8 mb-2">Usage Data</h4>
          <p>Usage Data is collected automatically when using the Service and may include:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Your Device’s IP address</li>
            <li>Browser type and version</li>
            <li>Pages visited and time spent</li>
            <li>Device identifiers</li>
            <li>Mobile OS and browser type</li>
          </ul>
          <p className="my-5">When You access our Service from a mobile device, We may also collect information such as:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Mobile device type and operating system</li>
            <li>Browser type and version</li>
            <li>Mobile browser type</li>
            <li>Unique device identifiers            </li>
          </ul>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Tracking Technologies and Cookies
          </h2>
          <p className="my-2">
            We use    <a
              href="/cookies"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            > Cookies </a> and similar technologies such as pixels, tags, and scripts to monitor Service activity and store information.
          </p>
          <p>You can choose to disable Cookies through Your browser settings, but some parts of our website may not function properly as a result.
          </p>

          <h4 className="text-lg font-semibold text-[#272d57] mt-4 mb-2">Types of Cookies Used</h4>
          <ul className="list-disc list-inside space-y-2 mt-4">
            <li><strong>Essential Cookies</strong>: Required for the website to function properly.</li>
            <li><strong>Preference Cookies</strong>: Remember user preferences and settings.</li>
            <li><strong>Analytics Cookies</strong>: Help us understand how users interact with our website.</li>
            <li><strong>Advertising Cookies</strong>:  Used to deliver relevant ads and measure campaign performance.</li>
          </ul>

          <p className="my-5"> For more information about the cookies we use and your choices regarding cookies, please visit our Cookies Policy.</p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">Use of Your Personal Data</h2>
          <p className="my-5">
            We use Your Personal Data for the following purposes:
          </p>
          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed">
            <li>To provide, operate, and maintain our Service.</li>
            <li>To manage Your Account and facilitate user access.</li>
            <li>To contact You via email, phone, or other communication channels.</li>
            <li>To send You updates, newsletters, and promotional offers (unless You opt out).</li>
            <li>To analyze usage patterns, improve user experience, and enhance functionality.</li>
            <li>To comply with legal obligations.</li>
            <li>To process business transactions such as mergers or acquisitions.</li>
            <li>For other internal purposes like analytics, data security, and fraud prevention.</li>
          </ul>


          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Retention of Your Personal Data
          </h2>
          <p>
            We retain Your Personal Data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements.
          </p>
          <p className="my-2">
            Usage Data may be retained for a shorter period, except when it is used to improve the Service’s security or functionality.

          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Transfer of Your Personal Data
          </h2>
          <p>
            Your information may be processed at our operating offices or by third parties located outside of Your jurisdiction. By submitting information to Us, You consent to this transfer, ensuring that all reasonable steps are taken to protect Your Personal Data in accordance with this Privacy Policy.
          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">Delete Your Personal Data</h2>
          <p>
            You have the right to request deletion of Your Personal Data collected by Us.
          </p>
          <p className="my-2">
            You may contact Us at<a href="mailto:info@makemybrandlabs.com" className="font-medium text-blue-600 hover:underline"> info@makemybrandlabs.com</a>
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>Request access to, update, or delete Your personal data.</li>
            <li>Withdraw consent for communications.</li>
          </ul>
          <p className="my-5">Please note that certain information may be retained for compliance with legal or contractual obligations.          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Disclosure of Your Personal Data
          </h2>
          <p>We may disclose Your Personal Data:</p>
          <ul className="list-disc list-outside space-y-2 pl-6 leading-relaxed mt-4">
            <li>To comply with legal obligations or government requests.</li>
            <li>To protect and defend the rights or property of the Company.</li>
            <li>To prevent or investigate wrongdoing in connection with the Service.</li>
            <li>In connection with business transfers such as mergers or acquisitions.</li>
          </ul>


          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Security of Your Personal Data
          </h2>
          <p>
            We implement appropriate technical and organizational measures to protect Your Personal Data. However, no transmission over the Internet or electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your information, We cannot guarantee absolute security.
          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">Children’s Privacy</h2>
          <p>
            Our Service is not intended for individuals under the age of 13, and We do not knowingly collect personal information from children. If You believe a child has provided Us with personal information, please contact Us immediately for removal.
          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Links to Other Websites
          </h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of such external sites. We encourage You to review the Privacy Policy of every site You visit.
          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">
            Changes to This Privacy Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. Updates will be posted on this page with a revised “Last Updated” date. We recommend reviewing this policy periodically for any changes.
          </p>

          {/* Section */}
          <h2 className="text-2xl font-semibold text-[#272d57] mt-12 mb-4">Contact Us</h2>
          <p>If You have any questions or concerns about this Privacy Policy, please contact Us:</p>
          <p className="mt-2 leading-relaxed">
            <strong>MMB Digital Labs (Make My Brand Labs)</strong><br />

            📧{" "}
            <a
              href="mailto:info@makemybrandlabs.com"
              className="font-medium text-blue-600 hover:underline"
            >
              info@makemybrandlabs.com
            </a>
            <br />

            🌐{" "}
            <a
              href="https://makemybrandlabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            >
              makemybrandlabs.com
            </a>
          </p>


        </div>
      </div>
    </div>

  );
}
